import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { DishImageSuggestionDTO } from './dishImageSuggestionsSlice';

interface DishImageSuggestionModalProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  dishId: string | null;
  suggestions: DishImageSuggestionDTO[];
  status: 'idle' | 'loading' | 'error';
  isGenerating?: boolean;
  generateError?: string;
  onSuggestionSelected: (suggestionId: string) => void;
}

const DishImageSuggestionModal: React.FC<DishImageSuggestionModalProps> = ({
  open,
  onClose,
  restaurantId,
  dishId,
  suggestions,
  status,
  isGenerating = false,
  generateError,
  onSuggestionSelected
}) => {
  // Filter out used suggestions (optional - can show them too)
  const availableSuggestions = suggestions.filter(s => !s.used);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Select dish image</DialogTitle>
      <DialogContent>
        {/* Generating state */}
        {isGenerating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 2 }}>
            <CircularProgress size={20} />
            <Typography>Generating suggestions from Google…</Typography>
          </Box>
        )}

        {/* Loading state (fetching, not generating) */}
        {!isGenerating && status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading suggestions…</Typography>
          </Box>
        )}

        {/* Idle state with suggestions */}
        {!isGenerating && status === 'idle' && availableSuggestions.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)'
              },
              gap: 2,
              mt: 1
            }}
          >
            {availableSuggestions.map((suggestion) => (
              <Box
                key={suggestion.id}
                onClick={() => {
                  onSuggestionSelected(suggestion.id);
                }}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid rgba(0,0,0,0.12)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'scale(1.02)'
                  }
                }}
              >
                <img
                  src={suggestion.url}
                  alt="Suggested dish"
                  style={{
                    width: '100%',
                    height: 120,
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Image+Error';
                  }}
                />
                {suggestion.source != null && (
                  <Box sx={{ p: 0.5, bgcolor: 'background.default', fontSize: '0.75rem', color: 'text.secondary' }}>
                    {suggestion.source}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Idle state with no suggestions */}
        {!isGenerating && status === 'idle' && availableSuggestions.length === 0 && suggestions.length === 0 && (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No suggestions available for this restaurant.
          </Typography>
        )}

        {/* Idle state with all suggestions used */}
        {!isGenerating && status === 'idle' && availableSuggestions.length === 0 && suggestions.length > 0 && (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No available suggestions. All suggestions have been used.
          </Typography>
        )}

        {/* Error state */}
        {status === 'error' && (
          <Typography color="error" sx={{ p: 2 }}>
            Failed to load suggestions.
          </Typography>
        )}

        {/* Generate error */}
        {generateError != null && (
          <Typography color="error" sx={{ mt: 2, p: 2 }}>
            {generateError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DishImageSuggestionModal;
